import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import Swal from 'sweetalert2';
import { FiCamera, FiXCircle } from 'react-icons/fi';
// import { BiFace } from 'react-icons/bi';
import axios from 'axios';

const CameraCapture2 = ({
    onClose,
    isOpen,
    onLoginSuccess,
}) => {

    const { t } = useTranslation();
    const webcamRef = useRef(null);
    const [cameraReady, setCameraReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const [deviceId, setDeviceId] = useState(null);

    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    const isAutofilled = useRef(false);
    const autofillPrevented = useRef(false);
    const [inputKey, setInputKey] = useState(Date.now());

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Check available cameras
    useEffect(() => {
        if (isOpen) {
            getCameraDevices();
        }
    }, [isOpen]);

    const getCameraDevices = async () => {
        setDeviceId(null);
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            if (videoDevices.length > 0) {
                setDeviceId(videoDevices[0].deviceId);
                setCameraError(null);
            }
        } catch (error) {
            console.error('Error getting camera devices:', error);
        }
    };

    // Handle user media errors
    const handleUserMediaError = useCallback((error) => {
        console.error('Camera error:', error);
        setCameraError(error.message);
        setCameraReady(false);

        Swal.fire({
            icon: 'error',
            title: t('Camera Error'),
            text: t('Unable to access camera. Please check permissions and try again.'),
            customClass: {
                confirmButton: 'gradient-button'
            }
        });
    }, [t]);

    // Handle user media success
    const handleUserMedia = useCallback(() => {
        setCameraReady(true);
        setCameraError(null);
    }, []);

    // Sync password when email is cleared
    useEffect(() => {
        if (email === '' && password !== '' && isAutofilled.current) {
            setPassword('');
            if (passwordRef.current) {
                passwordRef.current.value = '';
            }
            isAutofilled.current = false;
        }
    }, [email, password]);

    // Handle autofill detection
    useEffect(() => {
        const handleAutofill = () => {
            if (emailRef.current && emailRef.current.value && !email && !autofillPrevented.current) {
                setEmail(emailRef.current.value);
                isAutofilled.current = true;
            }
            if (passwordRef.current && passwordRef.current.value && !password && !autofillPrevented.current) {
                setPassword(passwordRef.current.value);
            }
        };

        setTimeout(() => {
            handleAutofill();
        }, 100);

        window.addEventListener('focus', handleAutofill);
        window.addEventListener('click', handleAutofill);

        return () => {
            window.removeEventListener('focus', handleAutofill);
            window.removeEventListener('click', handleAutofill);
        };
    }, [email, password]);

    const handleEmailChange = (e) => {
        const newValue = e.target.value.replace(/\s{2,}/g, " ");
        setEmail(newValue);

        if (newValue === '') {
            setPassword('');
            if (passwordRef.current) {
                passwordRef.current.value = '';
            }

            autofillPrevented.current = true;
            isAutofilled.current = false;
            setInputKey(Date.now());

            if (emailRef.current) {
                emailRef.current.setAttribute('autocomplete', 'off');
            }
            if (passwordRef.current) {
                passwordRef.current.setAttribute('autocomplete', 'off');
            }

            setTimeout(() => {
                if (emailRef.current) {
                    emailRef.current.setAttribute('autocomplete', 'username');
                }
                if (passwordRef.current) {
                    passwordRef.current.setAttribute('autocomplete', 'current-password');
                }
                autofillPrevented.current = false;
            }, 500);
        }
    };

    const handleEmailFocus = () => {
        if (autofillPrevented.current) {
            return;
        }
        if (emailRef.current) {
            emailRef.current.removeAttribute('readonly');
        }
        if (passwordRef.current) {
            passwordRef.current.removeAttribute('readonly');
        }
    };

    const startCapture = async () => {
        const imageSrc = webcamRef?.current?.getScreenshot();
        console.log({ imageSrc });
        setLoading(true);
        try {
            const loginResponse = await axios.post(`${process.env.REACT_APP_PORTAL_APP_BASE_URL}/biometric/authenticate`,
                {
                    type: 'FACE',
                    template: imageSrc,
                    email: email,
                    timestamp: new Date().toISOString(),
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        token: `${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
                    },
                }
            );

            const loginData = loginResponse.data.data;
            console.log('camera login response ===>', loginData);

            if (loginResponse.status === 200) {
                const token = loginData.token;
                setLoading(false);
                Swal.fire({
                    icon: 'success',
                    title: 'Login Successful',
                    text: `Welcome back, ${loginData.name || loginData.email || 'User'}!`,
                    timer: 2000,
                    showConfirmButton: false
                });
                onLoginSuccess(token, loginData);
            } else {
                setLoading(false);
                throw new Error(loginData.message || 'Authentication failed');
            }
        } catch (err) {
            setLoading(false);
            console.log('Face login error:', err);
            if (err?.response?.data?.message === "Liveness check failed") {
                setCameraError("Face is not verified please register your face first.");
                Swal.fire({
                    icon: 'error',
                    title: 'Face Login Failed',
                    text: 'Please position your face clearly within the frame, and try again.',
                    customClass: {
                        confirmButton: 'gradient-button'
                    }
                });
            }
            else if (err?.response?.data?.message === "Not verified") {
                setCameraError("Face is not verified please register your face first.");
                Swal.fire({
                    icon: 'error',
                    title: 'Face Login Failed',
                    text: 'Please register your face first',
                    customClass: {
                        confirmButton: 'gradient-button'
                    }
                });
            }
            else if (err?.response?.data?.message === "No face detected in the image") {
                Swal.fire({
                    icon: 'error',
                    title: 'Face Not Detected',
                    text: err.response?.data?.message,
                    customClass: {
                        confirmButton: 'gradient-button'
                    }
                });
                setCameraError(err.response?.data?.message);
            }
            else {
                setCameraError(err.response?.data?.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Login Failed',
                    text: err.response?.data?.message || err.message || 'Authentication failed',
                    customClass: {
                        confirmButton: 'gradient-button'
                    }
                });
            }
        }
    };

    console.log(cameraError);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm p-4 z-[9999]">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl mx-4 overflow-hidden transform transition-all animate-fadeIn">
                <div className="h-[400px] grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-2xl ring-2 ring-purple-500/20 hover:ring-purple-500/40 transition-all duration-500">
                        {/* {cameraError ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gradient-to-br from-red-600 to-red-700">
                                <div className="bg-white/20 p-4 rounded-full mb-4">
                                    <FiXCircle className="text-5xl" />
                                </div>
                                <p className="text-xl font-bold mb-2">{t('Camera Error')}</p>
                                <p className="text-sm text-center px-6 mb-6 max-w-md">{cameraError}</p>
                                <button
                                    onClick={getCameraDevices}
                                    className="px-8 py-3 bg-white text-red-600 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg flex items-center space-x-2"
                                >
                                    <span>{t('Retry')}</span>
                                    <FiCamera className="text-lg" />
                                </button>
                            </div>
                        ) : (
                            <> */}
                        <Webcam
                            ref={webcamRef}
                            audio={false}
                            screenshotFormat="image/jpeg"
                            videoConstraints={{
                                width: { ideal: 1080 },
                                height: { ideal: 600 },
                                facingMode: 'user',
                                deviceId,
                                aspectRatio: 16 / 9,
                            }}
                            onUserMedia={handleUserMedia}
                            onUserMediaError={handleUserMediaError}
                            className="w-full h-full object-cover"
                            style={{ transform: 'scaleX(-1)' }}
                        />

                        {/* Camera overlay guides */}
                        <div className="absolute inset-0 pointer-events-none">
                            {/* Face outline guide */}
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 border-2 border-white/30 rounded-3xl">
                                <div className="absolute -top-3 -left-3 w-6 h-6 border-t-4 border-l-4 border-purple-400 rounded-tl-xl"></div>
                                <div className="absolute -top-3 -right-3 w-6 h-6 border-t-4 border-r-4 border-purple-400 rounded-tr-xl"></div>
                                <div className="absolute -bottom-3 -left-3 w-6 h-6 border-b-4 border-l-4 border-purple-400 rounded-bl-xl"></div>
                                <div className="absolute -bottom-3 -right-3 w-6 h-6 border-b-4 border-r-4 border-purple-400 rounded-br-xl"></div>
                            </div>

                            {/* Camera status indicator */}
                            {cameraReady && (
                                <div className="absolute bottom-4 left-4 flex items-center space-x-2 bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                    <span>{t('Camera Ready')}</span>
                                </div>
                            )}
                        </div>
                        {/* </>
                        )} */}
                    </div>
                    <div className='h-full flex flex-col justify-between p-6'>
                        <div>
                            <div className="flex items-start justify-between relative z-10">
                                <div className="flex items-center space-x-4 mb-4">
                                    <div>
                                        <h2 className="text-2xl font-bold tracking-tight">{t('Face Login')}</h2>
                                        <p className="mt-1 text-gray-400 text-sm">
                                            {t('Secure biometric authentication')}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => onClose(false)}
                                    className="hover:bg-white/20 rounded-xl transition-all duration-300 group"
                                    aria-label="Close"
                                >
                                    <FiXCircle className="text-2xl group-hover:rotate-90 transition-transform duration-300" />
                                </button>
                            </div>
                            <input
                                key={`email-${inputKey}`}
                                ref={emailRef}
                                id="login-email"
                                type="email"
                                inputMode="email"
                                placeholder={t("Enter your email address")}
                                value={email}
                                onChange={handleEmailChange}
                                onFocus={handleEmailFocus}
                                className="w-full h-14 px-6 bg-white border-2 border-grey rounded-xl focus:outline-none focus:border-[#B03982] transition-all duration-300 text-gray-700 placeholder-gray-400"
                                name="email"
                                autoComplete="username"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                readOnly={autofillPrevented.current}
                            />
                        </div>
                        <button
                            onClick={startCapture}
                            disabled={!email || loading}
                            className={
                                `relative w-full py-4 rounded-xl font-bold text-md transition-all duration-500 transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center space-x-3 overflow-hidden group ${(cameraReady && email && !loading)
                                    ? 'bg-gradient-to-r from-[#B03982] to-[#733C86] text-white shadow-lg hover:shadow-xl'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                        >
                            <FiCamera className={`text-2xl ${cameraReady && email ? 'group-hover:rotate-12 transition-transform' : ''}`} />
                            <span>{loading ? t('Scanning') : t('Start Capture')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CameraCapture2;